<template>
  <v-container grid-list-xs class="pa-2">
    <v-layout row wrap>
      <v-flex :class="toolClass">
        <v-btn icon class="ma-0 text-red" :title="$t('clear')" v-if="value" @click="clearValue">
          <v-icon>clear</v-icon>
        </v-btn>
        <v-btn icon class="ma-0" :title="$t('add-note')" v-if="!(locked || note)" @click="addNote">
          <v-icon>note_add</v-icon>
        </v-btn>
        <v-chip small label outline :color="chipColor">IDX {{ index }}</v-chip>
      </v-flex>
      <v-flex :class="labelClass">
        <v-layout row wrap>
          <v-flex xs12 class="subtitle-1 mt-2">
            {{ translatedLabel }}
          </v-flex>
          <v-flex xs12 v-if="translatedDescription">
            <p><v-icon small>info</v-icon> {{ translatedDescription }}</p>
          </v-flex>
        </v-layout>
      </v-flex>
      <v-flex :class="inputClass">
        <!-- no data placeholder -->
        <p v-if="!value" class="grey--text">
          <v-icon small color="grey"
            @mouseover="help = index"
            @mouseleave="help = null"
          >help</v-icon>
          <span v-if="help === index">{{ $t('checklists.help-render-chart') }}</span>
        </p>
        <!-- graph -->
        <canvas :id="chartId"></canvas>
      </v-flex>
      <v-flex xs12 order-xs4 :style="noteStyle">
        <v-textarea
          v-model="note"
          :label="$t('note')"
          :disabled="locked"
          rows="1"
          auto-grow
          clearable
        ></v-textarea>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import translationMixin from './checklist-translation-mixin.es6'
import noteMixin from './checklist-note-mixin.es6'
import Chart from 'chart.js'

export default {
  name: 'checklist-input-graph',
  mixins: [translationMixin, noteMixin],
  props: {
    index: Number,
    label: String,
    description: String,
    default: Object,
    source: Object
  },
  data () {
    // initial state
    return {
      dataset: null,
      chart: null,
      help: null
    }
  },
  created () {
    const { value, note } = this.default
    // set default value
    if (Array.isArray(value)) {
      this.dataset = this.createSet(value[0])
      this.$nextTick(() => this.drawChart())
    }
    // set user note
    if ((/string/).test(typeof note)) {
      this.note = note
    }
  },
  computed: {
    value () {
      return this.dataset
    },
    valid () {
      if (this.value === null) {
        return true
      }
      return this.value && this.value.length > 0 // array of data
    },
    unit () {
      return this.source ? this.source.displayUnit : null
    },
    locked () {
      return this.$store.getters['checklist/isLocked']
    },
    chipColor () {
      const { value, valid } = this
      if (value === null || (Array.isArray(value) && value.length === 0)) {
        return 'gray'
      }
      if (valid) {
        return 'green'
      } else {
        return 'red'
      }
    },
    toolClass () {
      const { breakpoint } = this.$vuetify
      return {
        'text-right grey--text text--darken-2': true,
        'xs12': breakpoint.xs,
        'sm6': breakpoint.sm,
        'md3': breakpoint.mdAndUp,
        'order-xs1': breakpoint.xs,
        'order-sm2': breakpoint.sm,
        'order-md3': breakpoint.mdAndUp
      }
    },
    labelClass () {
      const { breakpoint } = this.$vuetify
      return {
        'xs12': breakpoint.xs,
        'sm6': breakpoint.sm,
        'md6': breakpoint.mdAndUp,
        'order-xs2': breakpoint.xs,
        'order-sm1': breakpoint.sm,
        'order-md2': breakpoint.mdAndUp
      }
    },
    inputClass () {
      const { breakpoint } = this.$vuetify
      return {
        'my-2 outline': true,
        'xs12': breakpoint.xs,
        'sm12': breakpoint.sm,
        'md3': breakpoint.mdAndUp,
        'order-xs3': breakpoint.xs,
        'order-sm3': breakpoint.sm,
        'order-md1': breakpoint.mdAndUp
      }
    },
    // chart
    chartId () {
      return `chart-${this.index}`
    }
  },
  methods: {
    drawChart () {
      const { dataset, chartId } = this
      if (this.chart) {
        this.chart.destroy()
      }
      const canvas = document.getElementById(chartId).getContext('2d')
      this.chart = new Chart(canvas, {
        type: 'line',
        options: {
          // showLines: false,
          plugins: {
            datalabels: {
              display: false
            }
          },
          layout: {
            padding: {
              top: 10
            }
          },
          legend: {
            display: false
          },
          tooltips: {
            enabled: true,
            displayColors: false,
            titleFontStyle: 'normal',
            callbacks: {
              title: (item) => `x: ${item[0].label}`,
              label: (item) => `y: ${item.value}`
            }
          },
          elements: {
            point: {
              pointStyle: 'circle',
              radius: 1,
              borderColor: 'rgba(0, 0, 0, 0.1)'
            }
          },
          scales: {
            xAxes: [{
              type: 'linear'
            }],
            yAxes: [{
              type: 'linear'
            }]
          }
        },
        data: {
          datasets: [{
            data: dataset
          }]
        }
      })
    },
    clearValue () {
      this.dataset = null
      this.$emit('value', { value: null })
    },
    createSet (data) {
      return data.x.map((x, i) => ({
        x: x,
        y: data.y[i]
      }))
    }
  },
  watch: {
    default: {
      handler (next, prev) {
        const { value, note } = next
        const { value: prevValue } = prev

        if (note !== null) {
          this.note = note
        }

        if (!value || !value.length) {
          if (this.chart) {
            this.chart.destroy()
          }
          return // no data to show
        }

        if (value && prevValue && value[0].length === prevValue[0].length) {
          return // no chart update
        }

        // there are two sets of data
        const data = value[0] // let's use the first set

        // data: { x: Number[], y: Number[] }
        this.dataset = this.createSet(data)
        this.$nextTick(() => this.drawChart())
      },
      deep: true
    }
  }
}
</script>
